import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isApiError, isOutOfScope } from '@/api/errors';
import { Button } from '@/shared/ui';

interface Props {
  error: unknown;
  onRetry?: (() => void) | undefined;
}

/**
 * Distinguishes "outside your scope" from a generic failure, because the two
 * need different words: one is a permissions boundary the user should
 * understand, the other is something that might work on retry.
 */
export function ErrorState({ error, onRetry }: Props) {
  const { t } = useTranslation();
  const outOfScope = isOutOfScope(error);
  const requestId = isApiError(error) ? error.requestId : '';

  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <AlertTriangle className="text-warning size-9" aria-hidden />
      <h2 className="text-base font-semibold">
        {outOfScope ? t('states.outOfScope') : t('states.error')}
      </h2>
      <p className="text-ink-muted max-w-md text-sm">
        {outOfScope ? t('states.outOfScopeHint') : t('states.errorHint')}
      </p>
      {requestId && (
        <code className="bg-surface-sunken text-ink-muted rounded px-2 py-1 text-xs">
          {requestId}
        </code>
      )}
      {onRetry && !outOfScope && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t('actions.retry')}
        </Button>
      )}
    </div>
  );
}
